import * as R from 'ramda';
import { DndContext } from '@dnd-kit/core';
import React, {
  useState,
  useCallback,
} from 'react';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
// helpers/constants
import * as G from '../../../../helpers';
// hocs
import { withDnDStartEnd } from '../../../../hocs';
// ui
import { Box, Span, Text, Flex, scrollableContainerCss3px } from '../../../../ui';
// component edit-report
import {
  getDnDReorderResult,
  ensureReportFieldDndId,
  getReportFieldSortableId,
} from '../../helpers';
// feature edit-report
import { defaultReportField } from './settings';
import { Fields, useFieldListHeight } from './fields';
//////////////////////////////////////////////////

const dndModifiers = [restrictToVerticalAxis, restrictToParentElement];

const SortHeader = (props) => {
  const {
    maxFields,
    addFieldText,
    addFieldsText,
    availableFields,
    reportFieldList,
    setReportFieldList,
    defaultFieldsCount,
    handleAddReportField,
    handleAddReportFields,
    setDefaultFieldsCount,
    setAvailableForReports,
  } = props;

  const count = R.compose(
    R.length,
    R.keys,
  )(reportFieldList);

  const inputStyles = {
    width: '22px',
    height: '22px',
    outline: 'none',
    fontSize: '12px',
    textAlign: 'center',
    marginRight: '15px',
    borderRadius: '2px',
    border: `1px solid ${G.getTheme('colors.darkGrey')}`,
  };

  const handleChange = (event) => {
    const value = R.compose(
      R.when(
        parseInt,
        R.clamp(1, 50),
      ),
      R.replace(/[^\d]/g, ''),
      R.trim,
      R.defaultTo(''),
    )(event.target.value);

    setDefaultFieldsCount(value);
  };

  const handleRemoveAll = () => {
    setAvailableForReports(availableFields);
    setReportFieldList(R.of(Array, ensureReportFieldDndId(defaultReportField)));
  };

  return (
    <Flex
      p='0 15px'
      height={40}
      width='100%'
      borderBottom='1px solid'
      justifyContent='space-between'
      color={G.getTheme('colors.light.black')}
      bg={G.getTheme('colors.light.lightGrey')}
      borderColor={G.getTheme('colors.lightGrey')}
    >
      <Span>{G.getWindowLocale('titles:fields', 'Fields')}</Span>
      {
        G.isNotNil(maxFields) &&
        <Span ml='2px'>{`${count}/${maxFields}`}</Span>
      }
      <Box
        ml={10}
        fontSize={12}
        color={G.getTheme('colors.light.blue')}
      >
        {
          R.gt(R.length(reportFieldList), 1) &&
          <Span
            mr={15}
            cursor='pointer'
            onClick={handleRemoveAll}
          >
            {G.getWindowLocale('titles:remove-all', 'Remove All')}
          </Span>
        }
        <Span
          mr='5px'
          cursor='pointer'
          onClick={handleAddReportFields}
        >
          {R.or(addFieldsText, G.getWindowLocale('titles:add-fields', 'Add Fields'))}
        </Span>
        <input
          type='text'
          style={inputStyles}
          onChange={handleChange}
          value={defaultFieldsCount}
        />
        <Span
          cursor='pointer'
          onClick={handleAddReportField}
        >
          {R.or(addFieldText, G.getWindowLocale('titles:add-field', 'Add Field'))}
        </Span>
        {/* TODO: we have performance issue here, have to add better solution */}
        {/* {
          R.isNil(maxFields) && maxCurrentFieldsLength &&
          <Span
            mr={15}
            cursor='pointer'
            onClick={handleAddAllReportField}
          >
            {G.getWindowLocale('titles:add-all-fields', 'Add All Fields')}
          </Span>
        } */}
      </Box>
    </Flex>
  );
};

const onDragEnd = (result, props) => {
  const { handleDragEnd, handleReportFieldSortDragEnd } = props;
  handleReportFieldSortDragEnd(result);
  handleDragEnd(result);
};

const FieldList = withDnDStartEnd((props) => {
  const { reportFieldList, handleDragEnd, handleDragStart } = props;

  const [draggingFieldId, setDraggingFieldId] = useState(null);

  const fieldsLength = R.keys(reportFieldList);
  const listHeight = useFieldListHeight();

  const handleFieldDragStart = useCallback((event) => {
    setDraggingFieldId(R.path(['active', 'id'], event));

    handleDragStart(event);
  }, [handleDragStart]);

  const handleFieldDragEnd = useCallback((event) => {
    setDraggingFieldId(null);

    const sortableIds = R.addIndex(R.map)(
      (field, index) => getReportFieldSortableId(field, index),
      reportFieldList,
    );
    const result = R.defaultTo({}, getDnDReorderResult(event, sortableIds));

    onDragEnd(result, props);
  }, [props, reportFieldList]);

  const handleFieldDragCancel = useCallback(() => {
    setDraggingFieldId(null);

    handleDragEnd();
  }, [handleDragEnd]);

  return (
    <DndContext
      modifiers={dndModifiers}
      id='edit-report-field-list'
      onDragEnd={handleFieldDragEnd}
      onDragStart={handleFieldDragStart}
      onDragCancel={handleFieldDragCancel}
    >
      <Flex flexDirection='column'>
        <SortHeader {...props} />
        <Box
          height={listHeight}
          css={scrollableContainerCss3px}
        >
          <Box>
            {
              G.isNilOrEmpty(fieldsLength) &&
              <Box p={15}>
                <Flex flexDirection='column'>
                  <Text fontSize={16} color={G.getTheme('colors.dark.grey')}>
                    {
                      G.getWindowLocale(
                        'messages:you-do-not-have-any-fields-for-this-report',
                        "You don't have any fields for this Report",
                      )
                    }
                  </Text>
                </Flex>
              </Box>
            }
          </Box>
          {
            G.isNotNilAndNotEmpty(fieldsLength) &&
            <Fields
              {...props}
              listHeight={listHeight}
              draggingFieldId={draggingFieldId}
            />
          }
        </Box>
      </Flex>
    </DndContext>
  );
});

export default FieldList;
